// Feedback #831 + #832 (Shawn) — one-click access to the pages used all day,
// from every page, without scrolling the sidebar.  Feedback #835 (James,
// 2026-09-30) made it customizable: each person picks which sidebar pages sit
// here and in what order (⚙️ Customize at the end of the bar), and an admin
// sets the firm's default in Settings → Features.
//
// Mounted by Layout at the top of the main content area, so it appears on
// every main-app page.  It is NOT in pop-out windows (PopupLayout) or the
// client portal (PortalLayout) — neither renders Layout.
//
// Which pages it shows (lib/quickBarItems.js holds the rule):
//   the person's own choice -> the firm default -> the built-in
//   Calendar / Matters / Time Entries / To-Do / Clients / Reports / Leads
//   (QUICK_BAR_BUILTIN_DEFAULT)
// read from GET /api/profile/preferences once on mount, then filtered to the
// pages this person can open — the sidebar's own permission + feature-flag
// rule (lib/navItems.js), so a button the user could not open is not shown.
// The last answer is cached per user in localStorage so a reload draws the
// person's bar straight away instead of flashing the default first; if the
// request fails the bar keeps that cached copy, or the built-in default.
//
// To-Do (the /tasks page) opens a small dropdown — wherever it sits in the
// person's order — of incomplete to-dos due today through the next
// DUE_SOON_DAYS days.  The badge counts exactly those; past-due items are
// deliberately left out of the badge and the list (see the render below for
// why) and get one "N earlier items are past due" line that opens the Tasks
// page on its Overdue tab.  The data is GET /api/calendar/tasks?slim=true — the
// Tasks page's own source and WHERE clause — and utils/todoDue.js holds the
// single shared definition of "due".  "Open To-Do list →" lands on the "Due
// within 10 days" tab, which ALSO lists overdue items, so that tab's count is
// the badge plus the past-due line.
//
// The to-dos are only fetched while To-Do is on this person's bar.
//
// Refresh: on mount, when the Tasks page changes something
// ('lextrack-todos-changed'), on a realtime event./task. push, and otherwise
// at most every 2 minutes while the tab is visible.  Deliberately NOT on
// timer saves (timerEntrySaved / lextrack_timer_broadcast): a time entry
// cannot change any to-do, and each refresh downloads the full to-do rows.  Failures are
// silent — the badge keeps its last value; no error banner on every page.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { NavLink, Link, useLocation } from 'react-router-dom'
import { format } from 'date-fns'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useFeatureFlags } from '../hooks/useFeatureFlags'
import {
  effectiveQuickBarKeys, visibleQuickBarItems, normalizeQuickBarKeys, QUICK_BAR_CHANGED_EVENT,
} from '../lib/quickBarItems'
import QuickBarCustomizeModal from './QuickBarCustomizeModal'
import { toFirmWallClock } from '../utils/eventTime'
import { parseDateOnly } from '../utils/format'
import {
  DUE_SOON_DAYS, TODO_LOOKBACK_DAYS, dueSoonSummary, dueLabel, isOverdue,
  firmDueDay, isDueAfterStartDay,
} from '../utils/todoDue'

const POLL_MS = 2 * 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000
const MAX_LISTED = 15
// Smallest gap kept between the To-Do dropdown and the window's left edge.
const DROPDOWN_EDGE_MARGIN = 8

// Per-viewer cache of the last preferences answer — a convenience only (it
// spares a reload the flash of the default bar); the server stays the source
// of truth and overwrites it on every mount.
const CACHE_PREFIX = 'lextrack_quick_bar:'
const EMPTY_BAR = { items: null, firmDefault: null }

function readCachedBar (userId) {
  if (!userId) return EMPTY_BAR
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + userId)
    if (!raw) return EMPTY_BAR
    const parsed = JSON.parse(raw)
    return {
      items: normalizeQuickBarKeys(parsed?.items),
      firmDefault: normalizeQuickBarKeys(parsed?.firmDefault),
    }
  } catch { return EMPTY_BAR }
}

function writeCachedBar (userId, bar) {
  if (!userId) return
  try { localStorage.setItem(CACHE_PREFIX + userId, JSON.stringify(bar)) } catch { /* private mode etc. */ }
}

const btnBase = 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm font-medium border transition-colors whitespace-nowrap'
// A button's label is hidden below 768 px wide (Tailwind `md` — the width
// where the side menu becomes the ☰ drawer: a phone or a narrow window), so
// the whole bar fits on about one line instead of wrapping into three
// (docs/specs/responsive-windowed.md).  The icon stays; the name is still the
// button's aria-label and its hover title.
const LABEL_CLASS = 'hidden md:inline'
const btnIdle = 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-primary-700'
const btnActive = 'bg-primary-600 border-primary-600 text-white'

function dueWhen (ev) {
  try {
    if (ev.due_date) {
      const d = parseDateOnly(ev.due_date)
      return d ? format(d, 'EEE MMM d') : ''
    }
    // An Amicus to-do (start day → due day): its due day, no time — the same
    // day its label counts to (utils/todoDue.js, James 2026-10-21).
    if (isDueAfterStartDay(ev)) {
      const d = firmDueDay(ev)
      return d ? format(d, 'EEE MMM d') : ''
    }
    // Firm wall clock, not the browser's — a 9am San Diego deadline reads 9am
    // from anywhere (the #821 / Taiwan lesson).
    const d = toFirmWallClock(ev.start_time)
    return d ? format(d, 'EEE MMM d, h:mm a') : ''
  } catch { return '' }
}

export default function QuickAccessBar () {
  const { user } = useAuth()
  const location = useLocation()
  const { isEnabled: isFeatureOn } = useFeatureFlags()
  const userId = user?.id

  // { items, firmDefault } exactly as GET /api/profile/preferences returns
  // quick_bar_items / quick_bar_firm_default (each an array of keys or null).
  const [bar, setBar] = useState(() => readCachedBar(userId))
  const [customizing, setCustomizing] = useState(false)
  const customizeBtnRef = useRef(null)

  useEffect(() => {
    if (!userId) return undefined
    let live = true
    setBar(readCachedBar(userId))
    api.get('/api/profile/preferences').then((res) => {
      if (!live) return
      const d = res?.data || {}
      setBar({
        items: normalizeQuickBarKeys(d.quick_bar_items),
        firmDefault: normalizeQuickBarKeys(d.quick_bar_firm_default),
      })
    }).catch(() => { /* silent — keep the cached copy, else the built-in default */ })
    return () => { live = false }
  }, [userId])

  useEffect(() => { writeCachedBar(userId, bar) }, [userId, bar])

  // A save anywhere in this window (this bar's own dialog, or an admin saving
  // the firm default in Settings) redraws the bar without a reload.  A save in
  // another tab arrives as a `storage` event on this person's cache key.
  useEffect(() => {
    const onChanged = (e) => {
      const d = e?.detail || {}
      setBar((prev) => ({
        items: 'items' in d ? normalizeQuickBarKeys(d.items) : prev.items,
        firmDefault: 'firmDefault' in d ? normalizeQuickBarKeys(d.firmDefault) : prev.firmDefault,
      }))
    }
    const onStorage = (e) => {
      if (userId && e?.key === CACHE_PREFIX + userId && e.newValue) setBar(readCachedBar(userId))
    }
    window.addEventListener(QUICK_BAR_CHANGED_EVENT, onChanged)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(QUICK_BAR_CHANGED_EVENT, onChanged)
      window.removeEventListener('storage', onStorage)
    }
  }, [userId])

  const visible = visibleQuickBarItems(effectiveQuickBarKeys(bar.items, bar.firmDefault), user, isFeatureOn)
  const showTodo = visible.some((i) => i.key === 'tasks')

  const [summary, setSummary] = useState(null) // { overdue, upcoming, total }
  const [failed, setFailed] = useState(false)
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)
  const lastFetchRef = useRef(0)
  const debounceRef = useRef(null)
  const inFlightRef = useRef(false)

  const refresh = useCallback(async () => {
    if (!showTodo || inFlightRef.current) return
    inFlightRef.current = true
    try {
      const now = Date.now()
      const from = new Date(now - TODO_LOOKBACK_DAYS * DAY_MS).toISOString()
      // +1 day: the server windows on start_time; "due within 10 days" counts
      // firm calendar days, so its last day can end just under 11 days out.
      const to = new Date(now + (DUE_SOON_DAYS + 1) * DAY_MS).toISOString()
      // slim + open_only: the handful of columns this bar renders, completed
      // items already dropped server-side (the full shape was ~724 kB per poll).
      const res = await api.get('/api/calendar/tasks', { params: { from, to, slim: 'true', open_only: 'true' } })
      setSummary(dueSoonSummary(res.data, DUE_SOON_DAYS))
      setFailed(false)
      lastFetchRef.current = Date.now()
    } catch {
      /* silent — keep the last-known badge; the dropdown says so if it has nothing */
      setFailed(true)
    } finally {
      inFlightRef.current = false
    }
  }, [showTodo])

  const refreshSoon = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => { debounceRef.current = null; refresh() }, 400)
  }, [refresh])

  useEffect(() => {
    if (!showTodo) { setSummary(null); return undefined }
    refresh()
    const tick = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      if (Date.now() - lastFetchRef.current >= POLL_MS - 1000) refresh()
    }, POLL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastFetchRef.current >= POLL_MS) refresh()
    }
    const onChange = () => refreshSoon()
    const onRealtime = (e) => {
      const t = e?.detail?.type || ''
      if (t.startsWith('event.') || t.startsWith('task.')) refreshSoon()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('lextrack-todos-changed', onChange)
    window.addEventListener('realtime', onRealtime)
    return () => {
      clearInterval(tick)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('lextrack-todos-changed', onChange)
      window.removeEventListener('realtime', onRealtime)
      if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null }
    }
  }, [showTodo, refresh, refreshSoon])

  // Close the dropdown on navigation, outside click, or Esc.
  useEffect(() => { setOpen(false) }, [location.pathname, location.search])
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    // stopPropagation: this listener is on `document`, so it runs before
    // window-level Esc handlers (Calendar's "Esc exits full screen").  An Esc
    // that closes this dropdown must not ALSO exit full screen.
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false) } }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Where the To-Do dropdown sits.  It hangs off the button's RIGHT edge,
  // which was safe only while To-Do was always the rightmost button.  Since
  // feedback #835 (James, 2026-09-30) it can be anywhere — first, or at the
  // start of a wrapped row — and in a narrow window the panel's left part
  // (where the to-do titles are) then ran off the screen, the same class as
  // #834.  Once it is drawn, measure it and slide it right just far enough
  // to keep DROPDOWN_EDGE_MARGIN clear of the window's left edge.  Layout
  // effect, so the corrected position is what gets painted.
  const panelRef = useRef(null)
  const [panelShift, setPanelShift] = useState(0)
  useLayoutEffect(() => {
    if (!open) { setPanelShift(0); return undefined }
    const place = () => {
      const wrap = wrapRef.current
      const panel = panelRef.current
      if (!wrap || !panel) return
      const leftEdge = wrap.getBoundingClientRect().right - panel.offsetWidth
      setPanelShift(Math.max(0, Math.round(DROPDOWN_EDGE_MARGIN - leftEdge)))
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [open])

  // Always rendered for a signed-in person — even with nothing on it, the ⚙️
  // control has to stay reachable so an emptied bar can be refilled.
  if (!user) return null

  const onSaved = ({ items, firmDefault }) => {
    const next = { items: normalizeQuickBarKeys(items), firmDefault: normalizeQuickBarKeys(firmDefault) }
    setBar(next)
    setCustomizing(false)
    try { window.dispatchEvent(new CustomEvent(QUICK_BAR_CHANGED_EVENT, { detail: next })) } catch { /* old browsers */ }
    // Deliberately NOT handed to ThemeContext (setPref).  ThemeContext PUTs
    // its whole cached preferences row on every change, so calling it here
    // turned each bar save into a second PUT of every theme field from this
    // tab's copy — which could revert a theme changed since on another
    // device.  The old bar in that cached row is harmless: ThemeContext drops
    // both quick-bar keys from its PUT (context/ThemeContext.test.js), and an
    // older bundle that still sends them also sends quick_bar_firm_default,
    // which PUT /api/profile/preferences treats as an echo and ignores
    // (api/src/routes/profile.js).
    setTimeout(() => { try { customizeBtnRef.current?.focus() } catch { /* ignore */ } }, 0)
  }
  const closeCustomize = () => {
    setCustomizing(false)
    setTimeout(() => { try { customizeBtnRef.current?.focus() } catch { /* ignore */ } }, 0)
  }

  const overdueN = summary?.overdue.length || 0
  const upcomingN = summary?.upcoming.length || 0
  const todoActive = location.pathname === '/tasks' || location.pathname.startsWith('/tasks/')
  // Shawn's ask (#832) was to-dos "due within the next ten days".  The badge
  // counts exactly that.  Overdue items are NOT in the badge or ahead of the
  // list: most are past appointments nobody marked complete (~34 firm-wide at
  // ship), so a permanent overdue count would be noise that trains people to
  // ignore the badge, and listing them first pushed the upcoming items — the
  // thing he asked for — off the end of the dropdown.  They get one line.
  const listed = summary ? summary.upcoming.slice(0, MAX_LISTED) : []
  const todoAria = summary
    ? `To-Do: ${upcomingN} due within ${DUE_SOON_DAYS} days${overdueN > 0 ? `, ${overdueN} past due` : ''}`
    : 'To-Do'

  const renderTodo = (item) => (
    <div key={item.key} className="relative" ref={wrapRef}>
      <button type="button"
        onClick={() => {
          // Opening re-checks only when the last answer is over 30 s old.
          if (!open && Date.now() - lastFetchRef.current > 30000) refresh()
          setOpen((v) => !v)
        }}
        aria-haspopup="true" aria-expanded={open} aria-label={todoAria}
        title={`To-dos due within the next ${DUE_SOON_DAYS} days`}
        className={`${btnBase} ${todoActive ? btnActive : btnIdle}`}>
        <span aria-hidden="true">{item.icon}</span>
        <span className={LABEL_CLASS}>{item.label}</span>
        {upcomingN > 0 && (
          <span className="min-w-[1.25rem] text-center text-xs font-bold px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-950"
            title={`${upcomingN} due within ${DUE_SOON_DAYS} days`}>{upcomingN}</span>
        )}
      </button>
      {open && (
        <div role="menu" ref={panelRef}
          style={panelShift ? { right: -panelShift } : undefined}
          className="absolute right-0 mt-1 w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase">
            Due within {DUE_SOON_DAYS} days
          </div>
          {summary === null ? (
            <p className="px-3 py-3 text-sm text-gray-500">{failed ? 'Could not load to-dos just now — try the To-Do list.' : 'Loading…'}</p>
          ) : listed.length === 0 ? (
            <p className="px-3 py-3 text-sm text-gray-500">Nothing due in the next {DUE_SOON_DAYS} days.</p>
          ) : (
            <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100">
              {listed.map((ev) => {
                const overdue = isOverdue(ev)
                return (
                  <li key={ev.id}>
                    <Link role="menuitem" to={`/tasks?due=${DUE_SOON_DAYS}&focus=${ev.id}`}
                      className="block px-3 py-2 hover:bg-gray-50">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm text-gray-800 font-medium line-clamp-2 break-words">{ev.title || '(untitled)'}</span>
                        <span className={`flex-shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${overdue ? 'bg-gray-900 text-white' : 'bg-amber-100 text-amber-800'}`}>
                          {dueLabel(ev)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 truncate">
                        {dueWhen(ev)}
                        {ev.matter_title && <span className="text-primary-600"> · 📁 {ev.matter_title}</span>}
                      </div>
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
          {summary && upcomingN > listed.length && (
            <p className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
              +{upcomingN - listed.length} more
            </p>
          )}
          {overdueN > 0 && (
            <Link to="/tasks?tab=overdue"
              className="block px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 border-t border-gray-100">
              {overdueN} earlier {overdueN === 1 ? 'item is' : 'items are'} past due and not marked complete →
            </Link>
          )}
          <Link to={`/tasks?due=${DUE_SOON_DAYS}`}
            className="block px-3 py-2 text-sm font-medium text-primary-600 hover:bg-gray-50 border-t border-gray-100">
            Open To-Do list →
          </Link>
        </div>
      )}
    </div>
  )

  return (
    // flex-1 + min-w-0: in Layout's toolbar row it takes the room beside the
    // Timers chip and wraps its buttons there, rather than claiming a whole
    // line and pushing the chip onto the next.
    <nav aria-label="Quick access" className="no-print flex flex-1 min-w-0 flex-wrap items-center justify-end gap-1.5 ml-auto">
      {visible.map((item) => (item.key === 'tasks' ? renderTodo(item) : (
        <NavLink key={item.key} to={item.path}
          aria-label={item.label} title={item.label}
          className={({ isActive }) => `${btnBase} ${isActive ? btnActive : btnIdle}`}>
          <span aria-hidden="true">{item.icon}</span>
          <span className={LABEL_CLASS}>{item.label}</span>
        </NavLink>
      )))}
      <button type="button" ref={customizeBtnRef}
        onClick={() => { setOpen(false); setCustomizing(true) }}
        aria-label="Customize quick bar" title="Customize this bar — choose which pages appear here"
        className="inline-flex items-center px-1.5 py-1 rounded-lg text-sm border border-transparent text-gray-400 hover:text-gray-700 hover:bg-gray-100 hover:border-gray-200 transition-colors">
        <span aria-hidden="true">⚙️</span>
        {visible.length === 0 && <span className="ml-1 text-xs">Customize quick bar</span>}
      </button>
      {customizing && (
        <QuickBarCustomizeModal
          userItems={bar.items}
          firmDefault={bar.firmDefault}
          user={user}
          isFeatureOn={isFeatureOn}
          onClose={closeCustomize}
          onSaved={onSaved}
        />
      )}
    </nav>
  )
}
